import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStatus } from '../contexts/StatusProvider';
import { useBrand, BRAND_COLORS } from '@equilateral/ui-shared';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { authenticate, Is_Loading, Is_Super_Admin, Auth_Status, Error_Message, Needs_Onboarding, Has_Client_Context } = useStatus();
  const navigate = useNavigate();
  const { brandCode } = useBrand();
  const brandInfo = BRAND_COLORS[brandCode] || BRAND_COLORS.equilateral;
  const brandName = brandInfo.name;
  const brandColor = brandInfo.primary;

  // Set page title for login page
  useEffect(() => {
    document.title = `${brandName} Platform — Sign In`;
  }, [brandName]);

  // CRITICAL: Watch Auth_Status and route automatically (Tim-Combo pattern)
  useEffect(() => {
    console.log('[LoginPage] useEffect fired:', { Auth_Status, Is_Super_Admin, Needs_Onboarding, Has_Client_Context });
    if (Auth_Status) {
      let target: string;

      if (Needs_Onboarding) {
        // New user without entitlements - go to onboarding
        target = '/onboarding';
      } else if (Is_Super_Admin && !Has_Client_Context) {
        // Super admin without client context - go to super dashboard
        target = '/super/dashboard';
      } else {
        // Regular user or super admin with client context - go to regular dashboard
        target = '/dashboard';
      }

      console.log('[LoginPage] Navigating to:', target);
      navigate(target, { replace: true });
    }
  }, [Auth_Status, Is_Super_Admin, Needs_Onboarding, Has_Client_Context, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Is_Loading) return;

    try {
      await authenticate(email, password);
      // Navigation happens automatically via useEffect
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {brandName} Platform
          </p>
        </div>

        {/* Authorized Use Notice */}
        <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-xs text-blue-700">
                <strong>Authorized Use Only:</strong> This system is for authorized users only.
                Unauthorized access or use may result in disciplinary action and/or civil and criminal penalties.
              </p>
            </div>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {Error_Message && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{Error_Message}</h3>
                </div>
              </div>
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={Is_Loading}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={Is_Loading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={Is_Loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brandColor, outline: 'none' }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
            >
              {Is_Loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>

        {/* Legal Footer */}
        <div className="mt-8 text-center text-xs text-gray-500 space-y-2">
          <p>
            By signing in, you agree to our{' '}
            <a href="https://equilateral.ai/terms" className="text-blue-600 hover:text-blue-500 underline" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://equilateral.ai/privacy" className="text-blue-600 hover:text-blue-500 underline" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </p>
          <p className="text-gray-400">
            &copy; {new Date().getFullYear()} {brandName}. All rights reserved.
          </p>
        </div>
      </div>
    </main>
  );
}
